import { useEffect, useState } from 'react';
import {
  REPORT_DIGESTS,
  defaultDashboardLayout,
  readDashboardLayout,
  type DashboardLayoutDto,
  type DashboardSection,
  type ReportDigestFrequency,
} from '@ecom/contracts';
import { ArrowLeftIcon, GripIcon } from '@/components/icons';
import { Segmented, Sheet } from '@/components/page';
import { SortableItem, SortableList } from '@/components/sortable';
import { Alert, Button, IconButton, Switch, cx } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { moveItem } from '@/lib/description-blocks';
import { SECTION_NEEDS, withOffered } from '@/lib/dashboard-layout';
import { date } from '@/lib/format';
import { can } from '@/lib/session';
import { useAuthStore } from '@/stores/auth.store';
import { usePreferencesStore } from '@/stores/preferences.store';

const SECTION_LABELS: Record<DashboardSection, { label: MessageKey; hint: MessageKey }> = {
  metrics: { label: 'overviewFigures', hint: 'overviewFiguresHint' },
  salesTrend: { label: 'salesTrend', hint: 'salesTrendHint' },
  topSellers: { label: 'topSellers', hint: 'topSellersHint' },
  actionQueue: { label: 'actionQueue', hint: 'queueOldestFirst' },
  lowStock: { label: 'lowStock', hint: 'lowStockHint' },
  customersToCall: { label: 'customersToCall', hint: 'customersToCallHint' },
  recentOrders: { label: 'recentOrders', hint: 'recentOrdersHint' },
  visits: { label: 'overviewVisits', hint: 'overviewVisitsHint' },
  myFollowUps: { label: 'myFollowUps', hint: 'myFollowUpsHint' },
  insights: { label: 'insightsTitle', hint: 'insightsHint' },
  target: { label: 'targetTitle', hint: 'targetHint' },
  live: { label: 'liveTitle', hint: 'liveHint' },
};

const DIGEST_LABELS: Record<ReportDigestFrequency, MessageKey> = {
  OFF: 'reportDigestOff',
  DAILY: 'reportDigestDaily',
  WEEKLY: 'reportDigestWeekly',
  MONTHLY: 'reportDigestMonthly',
};

/**
 * Customize (ADR-109): this person's overview — the sections in their order,
 * any of them hidden — and the store summary they get by email. Theirs only;
 * nothing here changes anyone else's screen or inbox.
 */
export function DashboardCustomize({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const user = useAuthStore((state) => state.user);
  const { preferences, saving, error, save, clearError } = usePreferencesStore();
  const [layout, setLayout] = useState<DashboardLayoutDto>(() => readDashboardLayout(preferences?.dashboard));
  const [digest, setDigest] = useState<ReportDigestFrequency>(preferences?.reportDigest ?? 'OFF');

  useEffect(() => clearError, [clearError]);

  // A section this person may not read is not offered — and keeps its place when they save.
  const offered = layout.sections.filter((section) => !SECTION_NEEDS[section.id] || can(user, SECTION_NEEDS[section.id]!));
  const reorder = (next: DashboardLayoutDto['sections']) => setLayout(withOffered(layout, next));
  const isDefault = JSON.stringify(layout) === JSON.stringify(defaultDashboardLayout());

  async function submit() {
    // The default saved as null: a section a later release adds then reaches them at its default.
    if (await save({ dashboard: isDefault ? null : layout, reportDigest: digest })) {
      onSaved();
      onClose();
    }
  }

  return (
    <Sheet
      open
      title={t('customizeOverview')}
      subtitle={t('customizeOverviewHint')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" className="me-auto" disabled={isDefault || saving} onClick={() => setLayout(defaultDashboardLayout())}>
            {t('resetToDefault')}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button disabled={saving} onClick={() => void submit()}>
            {t('save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {error ? <Alert>{error}</Alert> : null}

        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-ink">{t('reportDigest')}</h3>
          <p className="text-sm text-ink-muted">{t('reportDigestHint')}</p>
          <div>
            <Segmented
              label={t('reportDigest')}
              value={digest}
              onChange={setDigest}
              items={REPORT_DIGESTS.map((id) => ({ id, label: t(DIGEST_LABELS[id]) }))}
            />
          </div>
          {digest !== 'OFF' ? (
            <p className="text-xs text-ink-muted">
              {preferences?.email ? t('reportDigestTo', { email: preferences.email }) : null}
              {preferences?.reportDigestLastDay && preferences.reportDigest === digest
                ? ` ${t('reportDigestLast', { date: date(preferences.reportDigestLastDay, locale) })}`
                : ` ${t('reportDigestFirst')}`}
            </p>
          ) : null}
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-ink">{t('overviewSections')}</h3>
          <p className="text-sm text-ink-muted">{t('overviewSectionsHint')}</p>
          <SortableList items={offered} className="flex flex-col gap-2" onReorder={reorder}>
            {(section, index) => (
              <SortableItem key={section.id} id={section.id}>
                {(handle) => (
                  <div
                    className={cx(
                      'flex items-center gap-2 rounded-control border border-line bg-surface px-2 py-2',
                      section.hidden && 'bg-surface-sunken',
                    )}
                  >
                    <span
                      {...handle.attributes}
                      {...handle.listeners}
                      aria-label={t('dragToReorder')}
                      className="inline-flex size-8 shrink-0 cursor-grab items-center justify-center rounded-control text-ink-faint hover:bg-surface-sunken active:cursor-grabbing"
                    >
                      <GripIcon className="size-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={cx('block text-sm font-medium', section.hidden ? 'text-ink-muted' : 'text-ink')}>
                        {t(SECTION_LABELS[section.id].label)}
                      </span>
                      <span className="block truncate text-xs text-ink-muted">{t(SECTION_LABELS[section.id].hint)}</span>
                    </span>
                    <IconButton
                      label={t('moveUp')}
                      disabled={index === 0}
                      onClick={() => reorder(moveItem(offered, index, index - 1))}
                      className="size-8 border-0 disabled:pointer-events-none disabled:opacity-35"
                    >
                      <ArrowLeftIcon className="size-4 rotate-90" />
                    </IconButton>
                    <IconButton
                      label={t('moveDown')}
                      disabled={index === offered.length - 1}
                      onClick={() => reorder(moveItem(offered, index, index + 1))}
                      className="size-8 border-0 disabled:pointer-events-none disabled:opacity-35"
                    >
                      <ArrowLeftIcon className="size-4 -rotate-90" />
                    </IconButton>
                    <Switch
                      checked={!section.hidden}
                      label={t('showOnOverview')}
                      onChange={(on) =>
                        setLayout({ sections: layout.sections.map((entry) => (entry.id === section.id ? { ...entry, hidden: !on } : entry)) })
                      }
                    />
                  </div>
                )}
              </SortableItem>
            )}
          </SortableList>
        </section>
      </div>
    </Sheet>
  );
}
